// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { Body, Button, ExternalLink, LandingPage } from '@alto/design-system';
import { useQueryLandingPageConfigs } from '@alto/features';
import React from 'react';
import { getImage } from '~shared/assets/images/getImage';
import { getCompanyName } from '~shared/config/getCompanyName';
import { PHARMACY_NOTICES_URL, PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '~shared/constants';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';

type Props = {
  readonly handleGetStarted: () => void;
  readonly getStartedLoading: boolean;
  readonly tempPassword: string;
  readonly showTermsOfService?: boolean;
};

export const PartnershipLandingPage = ({
  handleGetStarted,
  getStartedLoading,
  tempPassword,
  showTermsOfService = false,
}: Props) => {
  const {
    isFetching,
    isSuccess,
    headerBackgroundColor,
    headerLogoUrl,
    welcomeHeader,
    welcomeBody,
    welcomeImageUrl,
    callToActionText,
    callToActionUrl,
    partnershipName,
  } = useQueryLandingPageConfigs({ tempPassword });
  const companyName = getCompanyName();
  const isFertilityUser = partnershipName === 'fertility';
  // HACK: Instead of adding company ids to [`OnboardingConfig` model in scriptdash],
  // replace any mention of "Alto" in copy with the current company name.
  const title = welcomeHeader?.replace('Alto', companyName);
  const body = welcomeBody?.replace('Alto', companyName);

  let illustrationSrc;

  if (isFertilityUser) {
    illustrationSrc = getImage('progynyWelcome');
  } else if (welcomeImageUrl) {
    illustrationSrc = { uri: welcomeImageUrl };
  } else {
    // fallback image which only renders when partnership image not set in onboarding config
    illustrationSrc = getImage('onboardingWelcome');
  }

  if (isFetching || !isSuccess) {
    return null;
  }

  return (
    <LandingPage
      withoutWebFramingElementHeights
      footerPlacementContext="webScreenWithoutPolicyFooter"
      HeaderBodyCopy={
        <Body>
          {body}
          {callToActionText && (
            <>
              {' '}
              {callToActionUrl ? (
                <ExternalLink href={callToActionUrl}>{callToActionText}</ExternalLink>
              ) : (
                callToActionText
              )}
            </>
          )}
        </Body>
      }
      backgroundColor={headerBackgroundColor || COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      NavBarCenterContent={
        <OnboardingNavBarLogo
          illustrationSrc={headerLogoUrl}
          isPartnerLogo={!isFertilityUser}
          partnershipName={partnershipName}
        />
      }
      title={title || `Welcome to ${companyName}`}
      illustrationSrc={illustrationSrc}
      illustrationSize="xxl"
      buttons={[
        <Button
          label="Get started"
          key="get-started"
          loading={getStartedLoading}
          onPress={handleGetStarted}
        />,
      ]}
    >
      {showTermsOfService ? (
        <Body>
          By clicking Get Started you have read and accept the{' '}
          <ExternalLink href={TERMS_OF_SERVICE_URL}>Terms of Service</ExternalLink>,{' '}
          <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>, and{' '}
          <ExternalLink href={PHARMACY_NOTICES_URL}>Pharmacy Notices</ExternalLink>.
        </Body>
      ) : null}
    </LandingPage>
  );
};
